import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cityStatusEmoji, cityStatusShort } from "./statusLabels";
import { IconText } from "./TaxiIcon";

// Выбор города: кнопка с текущим городом, панель — порталом поверх шапки.
// Городов много, поэтому список фильтруется поиском (город или область) и
// селектором области; строка пункта показывает область под названием.
const PANEL_MIN_WIDTH = 320; // px
const PANEL_GAP = 8;

export const normalizeCityQuery = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/\s+/g, " ")
    .trim();

const CityPicker = ({ cities, cityId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("");
  const [rect, setRect] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const focusReadyRef = useRef(false);
  const listId = useId();
  const current = cities.find((item) => String(item._id) === String(cityId));

  const sorted = useMemo(
    () => [...cities].sort((a, b) => a.name.localeCompare(b.name, "ru")),
    [cities]
  );
  const regions = useMemo(
    () =>
      [...new Set(sorted.map((item) => item.region?.name).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "ru")
      ),
    [sorted]
  );
  const normalizedQuery = normalizeCityQuery(query);
  const shown = useMemo(
    () =>
      sorted.filter((item) => {
        if (region && item.region?.name !== region) return false;
        if (!normalizedQuery) return true;
        return normalizeCityQuery(`${item.name} ${item.region?.name ?? ""}`).includes(
          normalizedQuery
        );
      }),
    [sorted, region, normalizedQuery]
  );

  const measure = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width });
  };

  useEffect(() => {
    if (!open) return undefined;
    measure();
    const onShift = () => measure();
    window.addEventListener("resize", onShift);
    window.visualViewport?.addEventListener("resize", onShift);
    window.visualViewport?.addEventListener("scroll", onShift);
    return () => {
      window.removeEventListener("resize", onShift);
      window.visualViewport?.removeEventListener("resize", onShift);
      window.visualViewport?.removeEventListener("scroll", onShift);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      focusReadyRef.current = false;
      return;
    }
    if (rect && !focusReadyRef.current) {
      focusReadyRef.current = true;
      searchRef.current?.focus();
    }
  }, [open, rect]);

  // Close on a click outside the picker or on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("click", onOutside);
    document.addEventListener("focusin", onOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onOutside);
      document.removeEventListener("focusin", onOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (item) => {
    onSelect(String(item._id));
    setOpen(false);
    setQuery("");
    setRegion("");
    triggerRef.current?.focus();
  };

  const onNavigate = (event) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const options = [...panelRef.current.querySelectorAll('[role="option"]')];
    const index = options.indexOf(event.target);
    if (index < 0) return;
    event.preventDefault();
    const next = event.key === "Home" ? 0
      : event.key === "End" ? options.length - 1
      : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
    options[next].focus();
    options[next].scrollIntoView?.({ block: "nearest" });
  };

  const onSearchKeyDown = (event) => {
    if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
    const options = [...(panelRef.current?.querySelectorAll('[role="option"]') ?? [])];
    if (!options.length) return;
    event.preventDefault();
    options[event.key === "ArrowUp" ? options.length - 1 : 0].focus();
    options[event.key === "ArrowUp" ? options.length - 1 : 0].scrollIntoView?.({ block: "nearest" });
  };

  const viewport = typeof window === "undefined" ? null : window.visualViewport;
  const vw = viewport?.width ?? (typeof window === "undefined" ? 360 : window.innerWidth);
  const vh = viewport?.height ?? (typeof window === "undefined" ? 800 : window.innerHeight);
  const viewportLeft = viewport?.offsetLeft ?? 0;
  const viewportTop = viewport?.offsetTop ?? 0;
  const width = Math.min(Math.max(0, vw - 16), Math.max(PANEL_MIN_WIDTH, rect?.width ?? PANEL_MIN_WIDTH));
  const left = Math.max(viewportLeft + 8, Math.min(rect?.left ?? 8, viewportLeft + vw - width - 8));
  const spaceBelow = rect ? viewportTop + vh - rect.bottom - PANEL_GAP - 8 : vh / 2;
  const spaceAbove = rect ? rect.top - viewportTop - PANEL_GAP - 8 : vh / 2;
  const openAbove = Boolean(rect) && spaceBelow < 220 && spaceAbove > spaceBelow;
  const maxHeight = Math.min(Math.max(0, vh - 16), Math.max(180, Math.min(vh * 0.62, openAbove ? spaceAbove : spaceBelow)));
  const anchorTop = rect
    ? openAbove
      ? rect.top - maxHeight - PANEL_GAP
      : rect.bottom + PANEL_GAP
    : vh / 2 - maxHeight / 2;
  const panelTop = Math.max(viewportTop + 8, Math.min(anchorTop, viewportTop + vh - maxHeight - 8));

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        className="press w-full min-h-[42px] flex items-center justify-between gap-2 rounded-[14px] border border-white/10 bg-[#252525] px-3 py-2 text-[1.3rem]"
        onClick={() => {
          if (!open) measure();
          setOpen((value) => !value);
        }}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
          event.preventDefault();
          measure();
          setOpen(true);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span aria-hidden><IconText>📍</IconText></span>
          <span className="font-semibold truncate text-primary-text">
            {current ? current.name : "Выберите город"}
          </span>
        </span>
        <span className="flex items-center gap-1 shrink-0 text-secondary-text text-[1.4rem]">
          {current && <span aria-hidden><IconText>{cityStatusEmoji(current.status)}</IconText></span>}
          <span aria-hidden><IconText>{open ? "🔼" : "🔽"}</IconText></span>
        </span>
      </button>

      {open && rect && createPortal(
        <div
          ref={panelRef}
          onKeyDown={onNavigate}
          style={{ position: "fixed", left, top: panelTop, width, maxHeight }}
          className="z-[100000] glass-strong rounded-2xl overflow-hidden flex flex-col shadow-2xl"
        >
          <div className="shrink-0 space-y-2 border-b border-white/10 p-3">
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Город или область"
              aria-label="Поиск города"
              autoComplete="off"
              className="w-full rounded-xl border border-white/15 bg-[#252525] px-3 py-2 text-[1.4rem] text-primary-text placeholder:text-secondary-text focus:border-accent focus:outline-none"
            />
            {regions.length > 0 && (
              <select
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                aria-label="Область"
                className="w-full rounded-xl border border-white/15 bg-[#252525] px-3 py-2 text-[1.3rem] text-primary-text focus:border-accent focus:outline-none"
              >
                <option value="">Все области</option>
                {regions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>
          <div id={listId} className="overflow-y-auto overscroll-contain p-2 flex flex-col gap-1.5" role="listbox" aria-label="Города">
            {shown.length === 0 && (
              <div className="px-3 py-6 text-center text-[1.3rem] text-secondary-text">
                Город не найден
              </div>
            )}
            {shown.map((item) => {
              const selected = String(item._id) === String(cityId);
              return (
                <button
                  key={item._id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => choose(item)}
                  className={`w-full flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl text-left ${
                    selected ? "btn-accent font-extrabold" : "glass"
                  }`}
                >
                  <span className="min-w-0">
                    <span className={`block truncate text-[1.55rem] leading-tight font-semibold ${selected ? "text-[#111111]" : "text-primary-text"}`}>
                      {item.name}
                    </span>
                    {item.region?.name && (
                      <span className={`block truncate text-[1.05rem] leading-tight ${selected ? "text-[#3a3200]" : "text-secondary-text"}`}>
                        {item.region.name}
                      </span>
                    )}
                  </span>
                  <span className={`shrink-0 inline-flex items-center gap-1.5 text-[1.1rem] leading-none font-normal ${selected ? "text-[#3a3200]" : "text-secondary-text"}`}>
                    <span aria-hidden className="inline-flex"><IconText size={13}>{cityStatusEmoji(item.status)}</IconText></span>
                    <span>{cityStatusShort(item.status)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CityPicker;
